"use client"
import { useRef, useState, useEffect } from 'react'
import { Container } from '@/components/shared/container'
import { motion, useInView } from 'framer-motion'
import { Play, Check, Loader, Leaf, Blend, SplineIcon, SplinePointerIcon, Binary, Code, Rocket, Sparkles } from 'lucide-react'

const ProcessDot = ({
  delay = 0,
  children,
}: {
  delay?: number
  children?: React.ReactNode
}) => {
  const [checked, setChecked] = useState(false)
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true })

  useEffect(() => {
    if (!isInView) return
    const timer = setTimeout(() => setChecked(true), delay * 1000 + 800)
    return () => clearTimeout(timer)
  }, [isInView, delay])

  return (
    <motion.div
      ref={ref}
      initial={{ scale: 0 }}
      viewport={{ once: true }}
      whileInView={{ scale: 1 }}
      transition={{ delay, duration: 0.3, type: 'spring' }}
      className='h-5 w-5  bg-blue-600 rounded-full relative flex items-center justify-center overflow-visible'
    >
      {!checked ? (
        <Loader size={14} className='animate-spin' />
      ) : (
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', duration: 0.3 }}
        >
          <Check size={12} className='text-white' strokeWidth={3} />
        </motion.div>
      )}
      {children}
    </motion.div>
  )
}

const Discovery = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      viewport={{ once: true }}
      whileInView={{ opacity: 1 }}
      transition={{ delay: 0.3, duration: 0.6 }}
      className='h-4 w-4 -translate-x-2 translate-y-8'
    >
      <div className='h-full w-full relative'>
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          viewport={{ once: true }}
          whileInView={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5, type: 'spring' }}
          className='border backdrop-blur z-5 flex items-center justify-center -translate-x-2.75 rounded-full -translate-y-15 absolute h-10 w-10 bg-background'
        >
          <Play size={17} className='text-foreground' />
        </motion.div>
        <div className='h-22 border-blue-500 rounded-tl-4xl border-dashed w-18 border-l border-t -translate-y-30 translate-x-2 absolute' />
        <div className='h-40 w-120 flex items-end -translate-y-65 translate-x-20 absolute'>
          <div className='flex items-center'>
            <motion.div
              initial={{ opacity: 0, x: -15 }}
              viewport={{ once: true }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5, duration: 0.4 }}
              className='relative bg-white h-10 flex items-center justify-center text-background pl-1 pr-5  w-max rounded-full'
            >
              <div className='bg-background text-white mr-2 h-8 w-10 flex items-center justify-center rounded-full bg-linear-to-bl from-muted'>
                <Leaf size={18} />
              </div>
              Discovery & Strategy

              <div className='absolute h-full w-30 right-0 rounded-full bg-linear-to-r from-transparent to-orange-700 via-blue-700 opacity-40 -z-5 blur-md scale-125' />
              <div className='absolute h-full w-30 right-0 rounded-full bg-linear-to-r from-transparent to-orange-700 via-blue-400 opacity-10 blur scale-125' />
            </motion.div>
            <motion.div
              initial={{ scaleX: 0 }}
              viewport={{ once: true }}
              whileInView={{ scaleX: 1 }}
              transition={{ delay: 0.7, duration: 0.3 }}
              style={{ transformOrigin: 'left' }}
              className='w-15 border-b border-dashed border-white/60'
            />
            <ProcessDot delay={0.9}>
              <motion.div
                initial={{ scaleY: 0 }}
                viewport={{ once: true }}
                whileInView={{ scaleY: 1 }}
                transition={{ delay: 1.0, duration: 0.3 }}
                style={{ transformOrigin: 'bottom' }}
                className='h-20 border-r left-2 -top-20 border-white/60 border-dashed absolute'
              />
              <motion.div
                initial={{ opacity: 0, y: 5 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1, duration: 0.3 }}
                className='left-2 -top-25 text-foreground/80 text-xs -translate-x-1/2 whitespace-nowrap absolute'
              >
                Define business goals
              </motion.div>
            </ProcessDot>
            <motion.div
              initial={{ scaleX: 0 }}
              viewport={{ once: true }}
              whileInView={{ scaleX: 1 }}
              transition={{ delay: 1.1, duration: 0.3 }}
              style={{ transformOrigin: 'left' }}
              className='w-17.5 border-b border-dashed border-white/60'
            />
            <ProcessDot delay={1.2}>
              <motion.div
                initial={{ scaleY: 0 }}
                viewport={{ once: true }}
                whileInView={{ scaleY: 1 }}
                transition={{ delay: 1.3, duration: 0.3 }}
                style={{ transformOrigin: 'bottom' }}
                className='h-10 border-r left-2 -top-10 border-white/60 border-dashed absolute'
              />
              <motion.div
                initial={{ opacity: 0, y: 5 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.4, duration: 0.3 }}
                className='left-2 -top-15 text-foreground/80 text-xs -translate-x-1/2 whitespace-nowrap absolute'
              >
                Align on requirements
              </motion.div>
            </ProcessDot>
            <motion.div
              initial={{ scaleX: 0 }}
              viewport={{ once: true }}
              whileInView={{ scaleX: 1 }}
              transition={{ delay: 1.4, duration: 0.3 }}
              style={{ transformOrigin: 'left' }}
              className='w-17.5 border-b border-dashed border-white/60'
            />
            <ProcessDot delay={1.5} />
          </div>
        </div>
      </div>
    </motion.div>
  )
}

const Design = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      viewport={{ once: true }}
      whileInView={{ opacity: 1 }}
      transition={{ delay: 0.9, duration: 0.6 }}
      className='h-4 w-4 rounded-full -translate-x-1.5 translate-y-8'
    >
      <div className='h-full w-full relative'>
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          viewport={{ once: true }}
          whileInView={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5, type: 'spring' }}
          className='border backdrop-blur flex items-center justify-center -translate-x-2.75 rounded-full translate-y-7.5 z-5 absolute h-10 w-10 bg-background'
        >
          <Blend size={17} className='text-foreground' />
        </motion.div>
        <div className='h-32 border-blue-500 rounded-bl-4xl border-dashed w-18 border-l border-b translate-y-12.5 translate-x-1.5 absolute' />
        <div className='h-40 w-120 flex items-start translate-y-40 translate-x-20 absolute'>
          <div className='flex items-center'>
            <motion.div
              initial={{ opacity: 0, x: -15 }}
              viewport={{ once: true }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.1, duration: 0.4 }}
              className='relative bg-white h-10 flex items-center justify-center text-background pl-1 pr-5 py-1 w-max rounded-full'
            >
              <div className='absolute h-full w-30 right-0 rounded-full bg-linear-to-r from-transparent to-orange-700 via-blue-700 opacity-40 -z-5 blur-md scale-125' />
              <div className='absolute h-full w-30 right-0 rounded-full bg-linear-to-r from-transparent to-orange-700 via-blue-400 opacity-10 blur scale-125' />
              <div className='bg-background text-white mr-2 h-8 w-10 flex items-center justify-center rounded-full bg-linear-to-bl from-muted'>
                <SplinePointerIcon size={18} />
              </div>
              Design & Architecture
            </motion.div>
            <motion.div
              initial={{ scaleX: 0 }}
              viewport={{ once: true }}
              whileInView={{ scaleX: 1 }}
              transition={{ delay: 1.3, duration: 0.3 }}
              style={{ transformOrigin: 'left' }}
              className='w-12.5 border-b border-dashed border-white/60'
            />
            <ProcessDot delay={1.5}>
              <motion.div
                initial={{ scaleY: 0 }}
                viewport={{ once: true }}
                whileInView={{ scaleY: 1 }}
                transition={{ delay: 1.6, duration: 0.3 }}
                style={{ transformOrigin: 'top' }}
                className='h-20 border-r left-2.5 top-5 border-white/60 border-dashed absolute'
              />
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.7, duration: 0.3 }}
                className='left-2 top-26 text-foreground/80 text-xs -translate-x-1/2 whitespace-nowrap absolute'
              >
                Plan system architecture
              </motion.div>
            </ProcessDot>
            <motion.div
              initial={{ scaleX: 0 }}
              viewport={{ once: true }}
              whileInView={{ scaleX: 1 }}
              transition={{ delay: 1.7, duration: 0.3 }}
              style={{ transformOrigin: 'left' }}
              className='w-17.75 border-b border-dashed border-white/60'
            />
            <ProcessDot delay={1.8}>
              <motion.div
                initial={{ scaleY: 0 }}
                viewport={{ once: true }}
                whileInView={{ scaleY: 1 }}
                transition={{ delay: 1.9, duration: 0.3 }}
                style={{ transformOrigin: 'top' }}
                className='h-10 border-r left-2 top-5 border-white/60 border-dashed absolute'
              />
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.0, duration: 0.3 }}
                className='left-2 top-16 text-foreground/80 text-xs -translate-x-1/2 whitespace-nowrap absolute'
              >
                Select the tech stack
              </motion.div>
            </ProcessDot>
            <motion.div
              initial={{ scaleX: 0 }}
              viewport={{ once: true }}
              whileInView={{ scaleX: 1 }}
              transition={{ delay: 2.0, duration: 0.3 }}
              style={{ transformOrigin: 'left' }}
              className='w-17 border-b border-dashed border-white/60'
            />
            <ProcessDot delay={2.1} />
          </div>
        </div>
      </div>
    </motion.div>
  )
}

const Development = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      viewport={{ once: true }}
      whileInView={{ opacity: 1 }}
      transition={{ delay: 1.5, duration: 0.6 }}
      className='h-4 w-4 -translate-x-2 translate-y-8'
    >
      <div className='h-full w-full relative'>
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          viewport={{ once: true }}
          whileInView={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5, type: 'spring' }}
          className='border backdrop-blur flex items-center justify-center -translate-x-2.75 rounded-full -translate-y-17 z-5 absolute h-10 w-10 bg-background'
        >
          <Binary size={17} className='text-foreground' />
        </motion.div>
        <div className='h-36 border-blue-500 rounded-tl-4xl border-dashed w-18 border-l border-t -translate-y-44 translate-x-2 absolute' />
        <div className='h-40 w-120 flex items-end -translate-y-78.5 translate-x-20 absolute'>
          <div className='flex items-center'>
            <motion.div
              initial={{ opacity: 0, x: -15 }}
              viewport={{ once: true }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.7, duration: 0.4 }}
              className='relative bg-white h-10 flex items-center justify-center text-background pl-1 pr-5  w-max rounded-full'
            >
              <div className='absolute h-full w-30 right-0 rounded-full bg-linear-to-r from-transparent to-orange-700 via-blue-700 opacity-40 -z-5 blur-md scale-125' />
              <div className='absolute h-full w-30 right-0 rounded-full bg-linear-to-r from-transparent to-orange-700 via-blue-400 opacity-10 blur scale-125' />
              <div className='bg-background text-white mr-2 h-8 w-10 flex items-center justify-center rounded-full bg-linear-to-bl from-muted'>
                <Code size={18} />
              </div>
              Development & Testing
            </motion.div>
            <motion.div
              initial={{ scaleX: 0 }}
              viewport={{ once: true }}
              whileInView={{ scaleX: 1 }}
              transition={{ delay: 1.9, duration: 0.3 }}
              style={{ transformOrigin: 'left' }}
              className='w-15 border-b border-dashed border-white/60'
            />
            <ProcessDot delay={2.1}>
              <motion.div
                initial={{ scaleY: 0 }}
                viewport={{ once: true }}
                whileInView={{ scaleY: 1 }}
                transition={{ delay: 2.2, duration: 0.3 }}
                style={{ transformOrigin: 'bottom' }}
                className='h-20 border-r left-2 -top-20 border-white/60 border-dashed absolute'
              />
              <motion.div
                initial={{ opacity: 0, y: 5 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.3, duration: 0.3 }}
                className='left-2 -top-25 text-foreground/80 text-xs -translate-x-1/2 whitespace-nowrap absolute'
              >
                Agile development cycles
              </motion.div>
            </ProcessDot>
            <motion.div
              initial={{ scaleX: 0 }}
              viewport={{ once: true }}
              whileInView={{ scaleX: 1 }}
              transition={{ delay: 2.3, duration: 0.3 }}
              style={{ transformOrigin: 'left' }}
              className='w-20 border-b border-dashed border-white/60'
            />
            <ProcessDot delay={2.4}>
              <motion.div
                initial={{ scaleY: 0 }}
                viewport={{ once: true }}
                whileInView={{ scaleY: 1 }}
                transition={{ delay: 2.5, duration: 0.3 }}
                style={{ transformOrigin: 'bottom' }}
                className='h-10 border-r left-2 -top-10 border-white/60 border-dashed absolute'
              />
              <motion.div
                initial={{ opacity: 0, y: 5 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.6, duration: 0.3 }}
                className='left-2 -top-15 text-foreground/80 text-xs -translate-x-1/2 whitespace-nowrap absolute'
              >
                Continuous QA checks
              </motion.div>
            </ProcessDot>
            <motion.div
              initial={{ scaleX: 0 }}
              viewport={{ once: true }}
              whileInView={{ scaleX: 1 }}
              transition={{ delay: 2.6, duration: 0.3 }}
              style={{ transformOrigin: 'left' }}
              className='w-20 border-b border-dashed border-white/60'
            />
            <ProcessDot delay={2.7} />
          </div>
        </div>
      </div>
    </motion.div>
  )
}

const Launch = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      viewport={{ once: true }}
      whileInView={{ opacity: 1 }}
      transition={{ delay: 2.1, duration: 0.6 }}
      className='h-4 w-4 -translate-x-2 translate-y-8'
    >
      <div className='h-full w-full relative'>
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          viewport={{ once: true }}
          whileInView={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5, type: 'spring' }}
          className='border backdrop-blur flex items-center justify-center -translate-x-2.75 rounded-full translate-y-10 z-5 absolute h-10 w-10 bg-background'
        >
          <Rocket size={17} className='text-foreground' />
        </motion.div>
        <div className='h-15 border-blue-500 rounded-bl-4xl border-dashed w-18 border-l border-b translate-y-12.5 translate-x-2 absolute' />
        <div className='h-40 w-120 flex items-start translate-y-22 translate-x-20 absolute'>
          <div className='flex items-center'>
            <motion.div
              initial={{ opacity: 0, x: -15 }}
              viewport={{ once: true }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 2.3, duration: 0.4 }}
              className='relative bg-white h-10 flex items-center justify-center text-background pl-1 pr-5  w-max rounded-full'
            >
              <div className='absolute h-full w-30 right-0 rounded-full bg-linear-to-r from-transparent to-orange-700 via-blue-700 opacity-40 -z-5 blur-md scale-125' />
              <div className='absolute h-full w-30 right-0 rounded-full bg-linear-to-r from-transparent to-orange-700 via-blue-400 opacity-10 blur scale-125' />
              <div className='bg-background text-white mr-2 h-8 w-10 flex items-center justify-center rounded-full bg-linear-to-bl from-muted'>
                <Sparkles size={18} />
              </div>
              Launch & Support
            </motion.div>
            <motion.div
              initial={{ scaleX: 0 }}
              viewport={{ once: true }}
              whileInView={{ scaleX: 1 }}
              transition={{ delay: 2.5, duration: 0.3 }}
              style={{ transformOrigin: 'left' }}
              className='w-15 border-b border-dashed border-white/60'
            />
            <ProcessDot delay={2.7}>
              <motion.div
                initial={{ scaleY: 0 }}
                viewport={{ once: true }}
                whileInView={{ scaleY: 1 }}
                transition={{ delay: 2.8, duration: 0.3 }}
                style={{ transformOrigin: 'top' }}
                className='h-20 border-r left-2.5 top-5 border-white/60 border-dashed absolute'
              />
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.9, duration: 0.3 }}
                className='left-2.5 top-27 text-foreground/80 text-xs -translate-x-1/2 whitespace-nowrap absolute'
              >
                Seamless deployment
              </motion.div>
            </ProcessDot>
            <motion.div
              initial={{ scaleX: 0 }}
              viewport={{ once: true }}
              whileInView={{ scaleX: 1 }}
              transition={{ delay: 2.9, duration: 0.3 }}
              style={{ transformOrigin: 'left' }}
              className='w-20 border-b border-dashed border-white/60'
            />
            <ProcessDot delay={3.0}>
              <motion.div
                initial={{ scaleY: 0 }}
                viewport={{ once: true }}
                whileInView={{ scaleY: 1 }}
                transition={{ delay: 3.1, duration: 0.3 }}
                style={{ transformOrigin: 'top' }}
                className='h-10 border-r left-2.5 top-5 border-white/60 border-dashed absolute'
              />
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 3.2, duration: 0.3 }}
                className='left-2.5 top-16 text-foreground/80 text-xs -translate-x-1/2 whitespace-nowrap absolute'
              >
                24/7 ongoing support
              </motion.div>
            </ProcessDot>
            <motion.div
              initial={{ scaleX: 0 }}
              viewport={{ once: true }}
              whileInView={{ scaleX: 1 }}
              transition={{ delay: 3.2, duration: 0.3 }}
              style={{ transformOrigin: 'left' }}
              className='w-14 border-b border-dashed border-white/60'
            />
            <ProcessDot delay={3.3} />
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default function Process() {
  return (
    <section id="process" className="hidden md:block border-border/40 relative overflow-hidden pb-20">
      <Container>
        <div className="px-5 relative z-10 m-auto max-w-5xl">
          <h2 className="text-3xl lg:text-5xl font-brand mb-">
            Our Process
          </h2>
          <p className="text-lg tracking-[-1px] text-muted-foreground max-w-170 font-light">
            A proven methodology that ensures successful project delivery from concept to launch.
          </p>
        </div>
      </Container>
      <div className='overflow-hidden flex items-center justify-center mt-15 h-150 w-full mb-5'>
        <motion.div
          className="absolute w-full h-150 hidden md:block"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <video
            src="/videos/8.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="object-cover h-full w-full absolute opacity-80 blur"
            style={{
              display: 'block',
              filter: 'brightness(0.3) saturate(1.4) contrast(1.4) hue-rotate(0deg)',
              mixBlendMode: 'screen'
            }}
          />
          <div className="absolute h-full w-full bg-linear-to-b to-background via-transparent from-background" />
        </motion.div>
        <div className='absolute grid grid-cols-70 h-150 w-full'>
          {Array.from({ length: 70 }).map((_, idx) => (
            <div
              key={idx}
              className={`flex items-center justify-center h-full w-px bg-linear-to-b ${idx % 5 === 0 ? " via-foreground/5" : "via-input/20"}`}
            >
              <div
                className={`relative h-20 w-px bg-linear-to-b  ${idx % 5 === 0 ? "via-foreground/30" : "via-input/50"}`}
              >
                {idx === 5 && <Discovery />}
                {idx === 15 && <Design />}
                {idx === 35 && <Development />}
                {idx === 40 && <Launch />}
              </div>
            </div>
          ))}
        </div>
        <div className='-z-10 w-full h-[2px] opacity-60 bg-muted relative flex items-start'>
          <motion.div
            initial={{ width: 0 }}
            viewport={{ once: true }}
            whileInView={{ width: '100%' }}
            transition={{ duration: 2, ease: 'easeInOut' }}
            className='blur h-full absolute bg-linear-to-r to-blue-500'
            style={{ left: 0 }}
          />
          <motion.div
            initial={{ width: 0 }}
            viewport={{ once: true }}
            whileInView={{ width: '100%' }}
            transition={{ duration: 2, ease: 'easeInOut' }}
            className='h-full absolute bg-blue-500'
            style={{ left: 0 }}
          />
        </div>
      </div>
    </section>
  )
}